import { Menu } from "@mantine/core";
import {
	CheckCircledIcon,
	Cross1Icon,
	DotsVerticalIcon,
} from "@modulz/radix-icons";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { cacheKey } from "../../hooks/cacheStateKey";
import { useApproveRequest, useEnableRequest } from "../../hooks/useRequests";
import type { Request } from "../../services/requests-service";
import { useState } from "react";
import { toast, Bounce } from "react-toastify";

const CellWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
`;

export const RequestActionsCell = ({
	data,
}: { data: Request }): JSX.Element => {
	const { mutate: approve, isLoading: isApprovingRequest } =
		useApproveRequest();
	const { mutate: enable, isLoading: isRevokingRequest } = useEnableRequest();
	const queryClient = useQueryClient();
	const [requestSubmitted, setRequestSubmitted] = useState(false);

	const handleApproval = () => {
		approve(
			{
				requestId: data.id,
				enable: true,
			},
			{
				onSuccess: () => {
					setRequestSubmitted(true);
          toast.success('Request is being processed. A notification will be sent upon completion.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            });
					// queryClient.invalidateQueries([cacheKey.accessRequests]);
					// queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
        onError: () => {
          setRequestSubmitted(false);
          toast.error('Something went wrong. Please try again.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            });
        }
			},
		);
	};

	const handleDecline = () => {
		approve(
			{
				requestId: data.id,
				enable: false,
			},
			{
				onSuccess: () => {
					setRequestSubmitted(true);
          toast.success('Request is being declined.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            });
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
        onError: () => {
          setRequestSubmitted(false);
          toast.error('Something went wrong. Please try again.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            });
            queryClient.invalidateQueries([cacheKey.accessRequests]);
					  queryClient.invalidateQueries([cacheKey.accessRequestStats]);
        }
			},
		);
	};

	const handleRevoke = () => {
		enable(
			{
				requestId: data.id,
				enable: false,
			},
			{
				onSuccess: () => {
					setRequestSubmitted(true);
          toast.success('Request is being revoked.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            });
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
        onError: () => {
          setRequestSubmitted(false);
          toast.error('Something went wrong. Please try again.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
            });
            queryClient.invalidateQueries([cacheKey.accessRequests]);
					  queryClient.invalidateQueries([cacheKey.accessRequestStats]);
        }
			},
		);
	};

	const handleUnRevoke = () => {
		enable(
			{
				requestId: data.id,
				enable: true,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries([cacheKey.accessRequests]);
					queryClient.invalidateQueries([cacheKey.accessRequestStats]);
				},
			},
		);
	};

	const showApproveButton =
		data?.approvalStatus === "PENDING" ||
		data?.approvalStatus === "REVOKED" ||
		data?.approvalStatus === "DECLINED";
	const showDeclineButton = data?.approvalStatus === "PENDING";
	const showRevokeButton = data?.approvalStatus === "APPROVED";

	const isMakingRequest = isApprovingRequest || isRevokingRequest;
	return (
		<CellWrapper>
			<Menu
				style={{
					color: "var(--textColor)",
					backgroundColor: "var(--accentBg)",
					border: "var(--accentBg)",
				}}
				shadow="md"
				control={
					<div>
						<DotsVerticalIcon />
					</div>
				}
			>
				<Menu.Label>Request Actions</Menu.Label>
				{showApproveButton && (
					<Menu.Item
						icon={
							<CheckCircledIcon
								style={{
									color:
										requestSubmitted || isMakingRequest
											? "var(--greyText)"
											: "var(--textColor)",
									backgroundColor: requestSubmitted || isMakingRequest
										? "transparent"
										: "var(--accentBg)",
								}}
							/>
						}
						style={{
							color:
								requestSubmitted || isMakingRequest
									? "var(--greyText)"
									: "var(--textColor)",
							fontWeight: "bold",
							backgroundColor: requestSubmitted || isMakingRequest
								? "transparent"
								: "var(--accentBg)",
						}}
						disabled={isMakingRequest || requestSubmitted}
						onClick={
							data?.approvalStatus === "REVOKED"
								? handleUnRevoke
								: handleApproval
						}
					>
						Approve
					</Menu.Item>
				)}
				{showDeclineButton && (
					<Menu.Item
						icon={
							<Cross1Icon
								style={{
									color:
										requestSubmitted || isMakingRequest
											? "var(--greyText)"
											: "#EC3D08",
								}}  
							/>
						}
						// style={{ color: "#EC3D08", fontWeight: "bold" }}
						style={{
							color:
								requestSubmitted || isMakingRequest
									? "var(--greyText)"
									: "#EC3D08",
							fontWeight: "bold",
							backgroundColor: "transparent",
						}}
						disabled={isMakingRequest || requestSubmitted}
						onClick={handleDecline}
					>
						Decline
					</Menu.Item>
				)}
				{showRevokeButton && (
					<Menu.Item
						icon={
							<Cross1Icon
								style={{
									color:
										requestSubmitted || isMakingRequest
											? "var(--greyText)"
											: "#EC3D08",
								}}
							/>
						}
						// style={{ color: "#EC3D08", fontWeight: "bold" }}
						style={{
							color:
								requestSubmitted || isMakingRequest
									? "var(--greyText)"
									: "#EC3D08",
							fontWeight: "bold",
							backgroundColor: "transparent",
						}}
						disabled={isMakingRequest}
						onClick={handleRevoke}
					>
						Revoke
					</Menu.Item>
				)}
			</Menu>
		</CellWrapper>
	);
};
